<template>
  <section
    :id
    :class="{
      container: !parentType,
    }"
  >
    <div ref="root" :style class="relative overflow-hidden bg-scheme-main-bis">
      <Transition
        leave-active-class="transition duration-500"
        leave-to-class="opacity-0"
      >
        <div
          v-if="thumbnailUrl && !isLoaded"
          class="absolute inset-0 z-100 px-[6.5%] transition tablet:bottom-[70px] tablet:pt-5 tablet:pb-3"
        >
          <img
            :src="thumbnailUrl"
            alt="Issuu thumbnail"
            class="relative block size-full object-contain"
            :class="{
              'blur-md tablet:blur-xl': !isEditing,
            }"
          />
        </div>
      </Transition>
      <ClientOnly>
        <iframe
          v-if="src && shouldRender && !isEditing"
          :src
          :title
          loading="lazy"
          scrolling="no"
          frameborder="0"
          allowfullscreen
          sandbox="allow-top-navigation allow-top-navigation-by-user-activation allow-downloads allow-scripts allow-same-origin allow-popups"
          class="relative z-50 size-full bg-scheme-main-bis transition"
          :class="{
            'opacity-0': !isLoaded,
          }"
          @load="onLoad"
        />
      </ClientOnly>
    </div>
  </section>
</template>

<script lang="ts" setup>
import type { CSSProperties } from 'vue'
import type { ParagraphIssuuFragment } from '#graphql-operations'
import { usePreferredDark } from '@vueuse/core'

const props = defineProps<ParagraphIssuuFragment>()

const isEditing = false

const { parentType, index, siblings } = defineBlokkli({
  bundle: 'issuu',
  globalOptions: ['bkHiddenGlobally', 'publicationChannels'],
})
const id = useParagraphAnchorId(index, siblings)

const root = ref<HTMLElement>()
const isDark = usePreferredDark()
const shouldRender = useRenderWhenVisible(root)
const isLoaded = ref(false)

const backgroundColor = computed(() => (isDark.value ? '#1e1e1e' : '#f7f7f7'))

const src = computed(() => {
  const path = props.media?.oembed?.iframeUrl?.path
  if (!path) {
    return null
  }

  const url = new URL(path)

  url.searchParams.append('hideIssuuLogo', 'true')
  url.searchParams.append('u', 'zuerichtourismus')
  url.searchParams.append('backgroundColor', backgroundColor.value)

  return url.toString()
})

const title = computed(() => props.media?.oembed?.title || props.media?.label)

const width = computed(() => props.media?.oembed?.width || 1565)
const height = computed(() => props.media?.oembed?.height || 1200)

const thumbnailUrl = computed(() => props.media?.oembed?.thumbnailUrl?.path)

const style = computed<CSSProperties>(() => {
  return {
    aspectRatio: `${width.value}/${height.value}`,
  }
})

function onLoad() {
  isLoaded.value = true
}
</script>
